/**
 * AssetSearchModal.jsx
 * Universal stock, ETF, crypto, commodity, and currency asset search catalog.
 * Supports instant search autocomplete, categorized quick-picks, real-time live quotes,
 * and 1-click selection to add to tezer portfolio.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as apiClient from '../services/apiClient';
import ThinkingOrbLoader from './ThinkingOrbLoader';

const QUICK_PICK_CATEGORIES = [
  {
    id: 'tech',
    label: '🚀 Popular Tech',
    symbols: ['AAPL', 'MSFT', 'NVDA', 'TSLA', 'AMZN', 'GOOGL', 'META']
  },
  {
    id: 'crypto',
    label: '🪙 Top Crypto',
    symbols: ['BTC-USD', 'ETH-USD', 'SOL-USD', 'BNB-USD', 'XRP-USD', 'ADA-USD']
  },
  {
    id: 'india',
    label: '🇮🇳 India (NSE)',
    symbols: ['INFY.NS', 'TCS.NS', 'RELIANCE.NS', 'HDFCBANK.NS', 'TATAMOTORS.NS', 'ICICIBANK.NS']
  },
  {
    id: 'gulf',
    label: '🇦🇪 UAE & Gulf',
    symbols: ['EMAAR.AE', 'DEWA.AE', 'DFM.AE', 'DIB.AE', '2222.SR']
  },
  {
    id: 'etf',
    label: '🌐 Major ETFs',
    symbols: ['SPY', 'VOO', 'QQQ', 'VGT', 'SCHD', 'VTI']
  },
  {
    id: 'commodities',
    label: '🟡 Commodities',
    symbols: ['GC=F', 'SI=F', 'CL=F', 'HG=F']
  }
];

export default function AssetSearchModal({
  isOpen,
  onClose,
  onSelectAsset,
  onManualCustomAsset
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('tech');
  const [results, setResults] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [searching, setSearching] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const searchInputRef = useRef(null);

  // Focus search input on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load quotes for quick-pick category symbols
  const loadCategoryQuotes = useCallback(async (symbols) => {
    setLoadingCategory(true);
    try {
      const qRes = await apiClient.request(`/market/quote?symbols=${symbols.join(',')}`);
      setQuotes(prev => ({ ...prev, ...(qRes || {}) }));
    } catch (err) {
      console.warn('Error loading category quotes:', err);
    } finally {
      setLoadingCategory(false);
    }
  }, []);

  // When category changes and search query is empty, load category quotes
  useEffect(() => {
    if (!searchQuery.trim()) {
      const cat = QUICK_PICK_CATEGORIES.find(c => c.id === activeCategory);
      if (cat) {
        loadCategoryQuotes(cat.symbols);
      }
    }
  }, [activeCategory, searchQuery, loadCategoryQuotes]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const searchRes = await apiClient.request(`/market/search?q=${encodeURIComponent(searchQuery)}`);
        const items = Array.isArray(searchRes) ? searchRes : [];
        setResults(items);

        // Fetch live quotes for top search results
        if (items.length > 0) {
          const topSymbols = items.slice(0, 8).map(i => i.symbol);
          const qRes = await apiClient.request(`/market/quote?symbols=${topSymbols.join(',')}`);
          setQuotes(prev => ({ ...prev, ...(qRes || {}) }));
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (!isOpen) return null;

  const handleChoose = (item) => {
    const quote = quotes[item.symbol] || {};
    const sym = item.symbol || '';
    const finalCurrency = quote.currency || item.currency || (sym.endsWith('.NS') || sym.endsWith('.BO') ? 'INR' : sym.endsWith('.AE') || sym.endsWith('.DU') ? 'AED' : sym.endsWith('.SR') ? 'SAR' : 'USD');
    const finalPrice = typeof quote.price === 'number' && quote.price > 0 ? quote.price : (typeof item.currentPrice === 'number' ? item.currentPrice : 0);

    const finalAsset = {
      symbol: item.symbol,
      name: item.name || quote.name || item.symbol,
      type: item.type || 'EQUITY',
      sector: item.sector || (item.type === 'CRYPTO' ? 'Cryptocurrency' : item.type === 'COMMODITY' ? 'Commodities' : 'Technology'),
      region: item.region || (finalCurrency === 'INR' ? 'Asia' : finalCurrency === 'AED' || finalCurrency === 'SAR' ? 'Middle East' : 'US'),
      currency: finalCurrency,
      currentPrice: finalPrice,
      marketCap: quote.marketCap ? (quote.marketCap > 10e9 ? 'Large' : quote.marketCap > 2e9 ? 'Mid' : 'Small') : 'N/A'
    };

    onSelectAsset(finalAsset);
  };

  const currentCat = QUICK_PICK_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 1000 }}>
      <div
        className="glass-panel modal-spring-enter modal-content-panel"
        style={{
          width: '100%',
          maxWidth: '560px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          gap: '14px',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0, color: 'var(--text-main)' }}>
              🔍 Add Investment Asset
            </h3>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Search global stocks, ETFs, crypto, and commodities with live prices
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-outflow)',
              fontWeight: '700',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Cancel
          </button>
        </div>

        {/* Search Bar Input */}
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by company or symbol (e.g. Apple, NVDA, BTC, Gold)..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 38px 12px 14px',
              borderRadius: '12px',
              background: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(0, 245, 212, 0.3)',
              color: 'var(--text-main)',
              fontSize: '13px',
              fontWeight: '600',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          {searching ? (
            <div
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ThinkingOrbLoader state="searching" size={20} color="var(--accent-cyan)" />
            </div>
          ) : searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              ✕
            </button>
          ) : null}
        </div>

        {/* Quick Picks Category Selector Tabs */}
        {!searchQuery.trim() && (
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none' }}>
            {QUICK_PICK_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  background: activeCategory === cat.id ? 'rgba(0, 245, 212, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                  color: activeCategory === cat.id ? 'var(--accent-cyan)' : 'var(--text-muted)',
                  border: activeCategory === cat.id ? '1px solid var(--accent-cyan)' : '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '10px',
                  padding: '6px 12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s'
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Assets List Results or Category Items */}
        <div
          className="hide-scrollbar"
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            minHeight: '220px',
            maxHeight: '340px'
          }}
        >
          {searchQuery.trim() ? (
            results.length === 0 && !searching ? (
              <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--text-muted)', fontSize: '12px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔎</div>
                No matching market assets found for &ldquo;{searchQuery}&rdquo;.
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => onManualCustomAsset && onManualCustomAsset(searchQuery)}
                    className="btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '11px' }}
                  >
                    ➕ Create Custom Manual Asset
                  </button>
                </div>
              </div>
            ) : (
              results.map(item => {
                const quote = quotes[item.symbol] || {};
                const hasQuote = typeof quote.price === 'number';
                const isPos = (quote.changePct || 0) >= 0;

                return (
                  <div
                    key={item.symbol}
                    onClick={() => handleChoose(item)}
                    className="hover-card"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--accent-cyan)' }}>
                            {item.symbol}
                          </span>
                          <span style={{ fontSize: '9px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', padding: '1px 5px', borderRadius: '4px', textTransform: 'uppercase' }}>
                            {item.type || 'EQUITY'}
                          </span>
                          <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                            {item.exchange}
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-main)', marginTop: '2px', fontWeight: '500' }}>
                          {item.name}
                        </span>
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      {typeof quote.price === 'number' && quote.price > 0 ? (
                        <>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-main)' }}>
                            {quote.currency || 'USD'} {quote.price > 1000 ? quote.price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : quote.price.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: isPos ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                            {isPos ? '+' : ''}{(quote.changePct || 0).toFixed(2)}%
                          </div>
                        </>
                      ) : (
                        <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontWeight: '700' }}>
                          + Select
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )
          ) : (
            // Quick Picks List
            currentCat && (
              currentCat.symbols.map(sym => {
                const quote = quotes[sym] || {};
                const isPos = (quote.changePct || 0) >= 0;
                const hasValidPrice = typeof quote.price === 'number' && quote.price > 0;

                return (
                  <div
                    key={sym}
                    onClick={() => handleChoose({ symbol: sym, name: quote.name || sym, type: currentCat.id === 'crypto' ? 'CRYPTO' : currentCat.id === 'etf' ? 'ETF' : currentCat.id === 'commodities' ? 'COMMODITY' : 'EQUITY' })}
                    className="hover-card"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--accent-cyan)' }}>
                          {sym}
                        </span>
                        <span style={{ fontSize: '9px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', padding: '1px 5px', borderRadius: '4px', textTransform: 'uppercase' }}>
                          {currentCat.id === 'crypto' ? 'CRYPTO' : currentCat.id === 'etf' ? 'ETF' : currentCat.id === 'commodities' ? 'COMMODITY' : 'EQUITY'}
                        </span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-main)', marginTop: '2px', display: 'block' }}>
                        {quote.name || sym}
                      </span>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      {hasValidPrice ? (
                        <>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-main)' }}>
                            {quote.currency || 'USD'} {quote.price > 1000 ? quote.price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : quote.price.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: isPos ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                            {isPos ? '+' : ''}{(quote.changePct || 0).toFixed(2)}%
                          </div>
                        </>
                      ) : loadingCategory ? (
                        <div style={{ width: '40px', height: '14px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
                      ) : (
                        <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontWeight: '700' }}>
                          + Select
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>

        {/* Footer: Manual Asset Option */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Can&apos;t find your asset?
          </span>
          <button
            type="button"
            onClick={() => onManualCustomAsset && onManualCustomAsset(searchQuery)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--accent-cyan)',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            ✏️ Enter Manually
          </button>
        </div>
      </div>
    </div>
  );
}
